<template>
  <div>
    <v-overlay
      :model-value="accessDenied"
      contained
      persistent
      scrim="background"
      opacity="1"
      class="align-center justify-center"
    >
      <div class="text-center">
        <v-icon size="80" color="error" class="mb-4">mdi-map-marker-question-outline</v-icon>
        <h1 class="text-headline-small font-weight-bold text-error mb-4">Section introuvable</h1>
        <p class="text-body1 text-grey mb-6">Cette section n'existe pas ou n'est plus publiée.</p>
        <v-btn color="primary" @click="$router.push('/')">Retour à l'accueil</v-btn>
      </div>
    </v-overlay>
    <v-toolbar class="river-detail-toolbar" :style="toolbarStyle" color="page-toolbar">
      <v-icon class="mr-8" @click="$router.go(-1)" title="Retour à la liste des sections">mdi-arrow-left</v-icon>
      <v-toolbar-title class="text-body-large">
        {{ section.river }} - {{ section.name }} - {{ section.class_level }} ({{ section.class_level_max }}) -
        {{ section.region }}
      </v-toolbar-title>
    </v-toolbar>
    <v-tabs v-model="tab" bg-color="page-toolbar" slider-color="page-toolbar-accent" show-arrows>
      <v-tab value="CurrentFlow" v-if="!restricted">
        <v-icon title="Conditions actuelles" size="large">mdi-chart-bell-curve</v-icon>
        <span class="ml-1 d-none d-md-flex">Conditions actuelles</span>
      </v-tab>
      <!-- Right after the current conditions: the camera shows them -->
      <v-tab value="Camera" v-if="!restricted && hasCamera">
        <v-icon title="Caméra" size="large">mdi-webcam</v-icon>
        <span class="ml-1 d-none d-md-flex">Caméra</span>
      </v-tab>
      <v-tab value="Dangers">
        <v-icon title="Dangers" size="large">{{ activeDangerCount ? "mdi-alert" : "mdi-alert-outline" }}</v-icon>
        <span class="ml-1 d-none d-md-flex">Dangers</span>
        <!-- Inline: a floating badge would be clipped by the tab -->
        <v-badge
          v-if="activeDangerCount"
          :content="activeDangerCount"
          color="danger"
          inline
          bordered
          class="danger-tab-badge"
          :title="`${activeDangerCount} danger${activeDangerCount > 1 ? 's' : ''} actif${
            activeDangerCount > 1 ? 's' : ''
          }`"
        ></v-badge>
      </v-tab>
      <v-tab value="Topography" v-if="!restricted && section.topo_info">
        <v-icon title="Topographie" size="large">mdi-map-legend</v-icon>
        <span class="ml-1 d-none d-md-flex">Topographie</span>
      </v-tab>
      <v-tab value="Videos" v-if="!restricted && section.video_info">
        <v-icon title="Vidéos" size="large">mdi-video-image</v-icon>
        <span class="ml-1 d-none d-md-flex">Vidéos</span>
      </v-tab>
      <v-tab value="Directions" v-if="!restricted && section.direction_info">
        <v-icon title="Directions" size="large">mdi-google-maps</v-icon>
        <span class="ml-1 d-none d-md-flex">Directions</span>
      </v-tab>
    </v-tabs>
    <v-alert v-if="restricted" type="info" variant="tonal" icon="mdi-lock-outline" class="ma-4 mb-0">
      <div class="d-flex flex-wrap align-center ga-3">
        <span class="flex-grow-1 restricted-text">
          Le débit, la topographie, les vidéos et les directions de cette section sont réservés aux membres. Les dangers
          signalés restent accessibles à tous.
        </span>
        <v-btn color="gold" variant="flat" :to="{ name: 'subscription' }">Devenir membre</v-btn>
      </div>
    </v-alert>
    <v-tabs-window v-model="tab" :touch="false">
      <v-tabs-window-item value="CurrentFlow" v-if="!restricted">
        <v-container>
          <v-alert v-if="activeDangerCount" type="error" variant="tonal" icon="mdi-alert" class="mb-4">
            <div class="d-flex flex-wrap align-center ga-3">
              <span class="flex-grow-1 restricted-text">
                {{ activeDangerCount }} danger{{ activeDangerCount > 1 ? "s" : "" }} actif{{
                  activeDangerCount > 1 ? "s" : ""
                }}
                signalé{{ activeDangerCount > 1 ? "s" : "" }} sur cette section.
              </span>
              <v-btn color="danger" variant="flat" @click="tab = 'Dangers'">Voir les dangers</v-btn>
            </div>
          </v-alert>
          <v-row>
            <v-col cols="12">
              <v-card>
                <v-card-title>Historique des 7 derniers jours</v-card-title>
                <v-card-text>
                  <v-skeleton-loader v-if="sectionLoading" type="image" height="400"></v-skeleton-loader>
                  <div v-else-if="sectionLevelHistory.level">
                    <LevelHistoryChart
                      :levelSectionHistory="sectionLevelHistory.level"
                      :minSugLevel="Number(section.min_sug_level)"
                      :maxSugLevel="Number(section.max_sug_level)"
                      :recommendedSugLevel="
                        section.recommended_sug_level !== null && section.recommended_sug_level !== ''
                          ? Number(section.recommended_sug_level)
                          : null
                      "
                      :minCotation="section.class_level_min"
                      :baseCotation="section.class_level"
                      :maxCotation="section.class_level_max"
                      :flowLevels="section.flow_levels || []"
                      :primaryTitle="primaryFormula ? primaryFormula.title : ''"
                      :primaryAccuracy="primaryFormula ? primaryFormula.accuracy : ''"
                      :comparisonSeries="chartFormulas"
                    />
                    <!-- With comparison lines the legend names each calculation with its accuracy; alone, the main
                         calculation shows it here (not while the admins haven't rated it) -->
                    <p v-if="showPrimaryAccuracy" class="text-body-small text-medium-emphasis mt-2 mb-0">
                      <v-icon size="small" class="mr-1">mdi-bullseye-arrow</v-icon>
                      {{ primaryFormula.title }} · précision {{ accuracyLabel(primaryFormula.accuracy) }}
                    </p>
                  </div>
                  <v-empty-state
                    v-else
                    icon="mdi-chart-line"
                    size="64"
                    title="Aucune donnée historique"
                    text="Il n'y a pas de donnée historique pour cette section."
                  ></v-empty-state>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-card>
                <v-card-title>Description</v-card-title>
                <v-card-text>
                  <div class="section-description" v-html="section.description || ''"></div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-card>
                <v-card-title>Conditions actuelles</v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col>
                      <p>
                        <strong>Débit:</strong> <br />
                        <level-marker :status="section.level_status" :size="22" class="mr-1"></level-marker>
                        <span :class="getColor(section)">{{ section.current_level }}</span>
                        <AcronymTooltip code="mcs" />
                      </p>
                    </v-col>
                    <v-col>
                      <p>
                        <strong>Débit recommandé:</strong> <br />
                        Entre {{ section.min_sug_level }}
                        <AcronymTooltip code="mcs" />
                        et {{ section.max_sug_level }}
                        <AcronymTooltip code="mcs" />
                      </p>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <p>
                        <strong>Tendance:</strong> <br />
                        <v-icon v-if="Math.sign(section.current_trend) == 1" size="x-small" color="green-darken-2">
                          mdi-arrow-up
                        </v-icon>
                        <v-icon v-if="Math.sign(section.current_trend) == -1" size="x-small" color="red">
                          mdi-arrow-down
                        </v-icon>
                        {{ Math.abs(section.current_trend) }}
                        <AcronymTooltip code="mch" />
                      </p>
                    </v-col>
                    <v-col>
                      <p>
                        <strong>Mise à jour il y a :</strong> <br />
                        {{ getLastUpdate() }}
                      </p>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-tabs-window-item>
      <v-tabs-window-item value="Camera" v-if="!restricted && hasCamera">
        <CameraPhotos :section="section"></CameraPhotos>
      </v-tabs-window-item>
      <v-tabs-window-item value="Dangers">
        <Dangers
          :section="section"
          :dangers="dangers"
          :loading="dangersLoading"
          :is-connected="isConnected"
          :is-member="isMember"
          :is-admin="isAdmin"
          @changed="loadDangers"
        ></Dangers>
      </v-tabs-window-item>
      <v-tabs-window-item value="Topography" v-if="!restricted && section.topo_info">
        <Topos :section="section"></Topos>
      </v-tabs-window-item>
      <v-tabs-window-item value="Videos" v-if="!restricted && section.video_info">
        <Videos :section="section"></Videos>
      </v-tabs-window-item>
      <v-tabs-window-item value="Directions" v-if="!restricted && section.direction_info">
        <Directions :section="section"></Directions>
      </v-tabs-window-item>
    </v-tabs-window>
  </div>
</template>

<script>
  import { RiversService } from "@/services/Rivers.service";
  import SectionsService from "@/store/modules/sections";
  import { UserService } from "@/store/modules/user";
  import { auth } from "@/firebase";
  import { DangersService } from "@/store/modules/dangers";
  import AcronymTooltip from "@/components/AcronymTooltip";
  import LevelHistoryChart from "@/components/LevelHistoryChart";
  import LevelMarker from "@/components/LevelMarker.vue";
  import Dangers from "@/components/pages/river/components/Dangers";
  import Directions from "@/components/pages/river/components/Directions";
  import Topos from "@/components/pages/river/components/Topos";
  import Videos from "@/components/pages/river/components/Videos";
  import CameraPhotos from "@/components/pages/river/components/CameraPhotos.vue";
  import { applySeoMeta, buildSectionSeo, signalPrerenderReady } from "@/utils/seo";
  import { sectionHasCamera } from "@/utils/cameras";
  import { levelTextClass } from "@/utils/levelStatus";
  import { getAccuracyLabel } from "@/utils/flowFormulas";
  import { canReadSectionLevels, withSectionLevels } from "@/utils/sectionLevels";

  export default {
    components: {
      AcronymTooltip,
      LevelHistoryChart,
      LevelMarker,
      Dangers,
      Directions,
      Videos,
      Topos,
      CameraPhotos,
    },
    computed: {
      showPrimaryAccuracy() {
        const accuracy = this.primaryFormula ? this.primaryFormula.accuracy : "";
        return Boolean(accuracy && accuracy !== "unknown" && !this.chartFormulas.length);
      },
      hasCamera() {
        return sectionHasCamera(this.section);
      },
      // Without access (members-only section), only the Dangers tab is shown.
      restricted() {
        return this.accessChecked && !this.hasAccessToSection();
      },
      activeDangerCount() {
        return this.dangers.filter((danger) => !danger.archived).length;
      },
      toolbarStyle() {
        const appTop = this.$vuetify && this.$vuetify.application ? this.$vuetify.application.top : 0;
        const appBarHeight = this.$root && this.$root.appBarHeight ? this.$root.appBarHeight : 64;
        const topOffset = appTop > 0 ? appTop : appBarHeight;
        return {
          // Keep toolbar sticky directly under the app bar using Vuetify's reactive layout top.
          top: `${Math.max(topOffset - 1, 0)}px`,
        };
      },
    },
    data() {
      return {
        tab: null,
        section: {},
        sectionLevelHistory: [],
        // Other formulas the admins show on the chart, and the main one { id, title, accuracy } (dataExtractFunctions.js)
        chartFormulas: [],
        primaryFormula: null,
        sectionLoading: true,
        sectionVideos: {},
        isConnected: false,
        isMember: false,
        isAdmin: false,
        accessDenied: false,
        accessChecked: false,
        dangers: [],
        dangersLoading: true,
        prerenderReadySignaled: false,
        level_unknown: "unknown",
        level_low: "low",
        level_recommended: "recommended",
        level_high: "high",
      };
    },
    async mounted() {
      try {
        this.sectionLoading = true;

        // Check user membership status before loading section
        await this.checkUserMembership();

        // Load section info
        await this.getSectionInfo();
        this.updateSeoMeta();

        if (!this.section || !this.section.id) {
          this.accessDenied = true;
          console.error("Section not found, redirecting to the home page.");
          // Auto-redirect after 3 seconds to allow user to see the message
          setTimeout(() => {
            this.$router.push("/");
          }, 3000);
          return;
        }

        // Dangers are public; the rest of the section is for members unless it is free access.
        this.accessChecked = true;
        if (this.restricted || this.$route.query.onglet === "dangers") {
          this.tab = "Dangers";
        }
        await Promise.all([this.restricted ? null : this.getSectionLevelHistory(this.section.id), this.loadDangers()]);
        // Again with the dangers, before the page is prerendered
        this.updateSeoMeta();
      } catch (error) {
        console.error("Error during data loading:", error);
      } finally {
        this.sectionLoading = false;
        this.signalPageReady();
      }
    },
    methods: {
      signalPageReady() {
        if (this.prerenderReadySignaled) {
          return;
        }

        this.$nextTick(() => {
          signalPrerenderReady();
          this.prerenderReadySignaled = true;
        });
      },
      async checkUserMembership() {
        try {
          this.isConnected = Boolean(auth.currentUser);
          if (!auth.currentUser) {
            this.isMember = false;
            return;
          }

          const userRoles = await UserService.actions.getUserRoles(auth.currentUser.uid);
          this.isMember = userRoles ? userRoles.isMember : false;
          this.isAdmin = userRoles ? userRoles.isAdmin : false;
        } catch (error) {
          console.error("Error checking user membership:", error);
          this.isMember = false;
          this.isAdmin = false;
        }
      },
      hasAccessToSection() {
        // User has access if:
        // 1. Section is marked as free access, OR
        // 2. User is a premium member
        if (!this.section) {
          return false;
        }
        return this.section.free_access === true || this.isMember;
      },
      async getSectionInfo() {
        // The router's slug ignores a trailing slash, unlike the raw URL path
        const aliasInRequest = String(this.$route.params.slug || "").trim();

        if (!aliasInRequest || typeof aliasInRequest !== "string") {
          console.error("Invalid alias in request:", aliasInRequest);
          return;
        }

        try {
          // Use SectionsService.actions directly like in RiverList.vue
          const sections = await SectionsService.actions.findSectionsByAlias(aliasInRequest);

          if (sections && sections.length > 0) {
            // The current level is in sectionsLevels, readable by members (everyone for free sections)
            const section = sections[0];
            const levels = canReadSectionLevels(section, this.isMember)
              ? await SectionsService.actions.findSectionLevels(section.id)
              : null;
            this.section = withSectionLevels(section, levels);
          } else {
            console.error("No section found for alias:", aliasInRequest);
          }
        } catch (error) {
          console.error("Error fetching section by alias:", error);
        }
      },
      async loadDangers() {
        this.dangersLoading = true;
        try {
          this.dangers = await DangersService.actions.getSectionDangers(this.section.id);
        } catch (error) {
          console.error("Error fetching section dangers:", error);
        } finally {
          this.dangersLoading = false;
        }
      },
      async getSectionLevelHistory(id) {
        if (!id || typeof id !== "string") {
          console.error("Invalid section ID:", id);
          return;
        }

        try {
          // Use SectionsService.actions directly like in RiverList.vue
          const sectionLevelHistoryDoc = await SectionsService.actions.findSectionFlowHistory(id);

          if (sectionLevelHistoryDoc) {
            const sectionLevelHistory = sectionLevelHistoryDoc.data();
            this.sectionLevelHistory = JSON.parse(sectionLevelHistory.level_history);
            this.chartFormulas = this.parseChartFormulas(sectionLevelHistory.chart_formulas);
            this.primaryFormula = sectionLevelHistory.primary_formula || null;
          } else {
            console.error("No level history found for section ID:", id);
          }
        } catch (error) {
          console.error("Error fetching section level history:", error);
        }
      },
      parseChartFormulas(value) {
        if (!value) return [];
        try {
          const formulas = JSON.parse(value);
          return Array.isArray(formulas) ? formulas : [];
        } catch {
          return [];
        }
      },
      accuracyLabel(accuracy) {
        return getAccuracyLabel(accuracy).toLowerCase();
      },
      getTotalRapids(section) {
        return RiversService.getTotalRapids(section);
      },
      getLastUpdate() {
        return RiversService.getLastUpdate(this.section.level_last_update);
      },
      getColor(obj) {
        return levelTextClass(obj.level_status);
      },
      updateSeoMeta() {
        applySeoMeta(
          buildSectionSeo(this.section, {
            hasAccess: this.hasAccessToSection(),
            dangers: this.dangers,
          }),
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  .danger-tab-badge :deep(.v-badge__badge) {
    font-weight: 700;
  }
  .restricted-text {
    flex-basis: 280px;
  }
  .river-detail-toolbar {
    position: sticky;
    z-index: 1;
  }

  .section-description :deep(p:last-child) {
    margin-bottom: 0;
  }
</style>
